import React, { useCallback, useEffect, useState } from 'react';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { fetchSettingsPortal, atualizarSettingPortal } from '../../../api/portalCotacoes/portalCotacoesApi';
import { notify } from '../../../shared/feedback/notify';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';

/**
 * Aba "Resposta do fornecedor" da configuração de Cotações (portal-cotacoes/at-16).
 *
 * Reúne o que a reunião de Compras de 28/10/2026 decidiu sobre o link do fornecedor:
 *  - as CONDIÇÕES DA PEÇA (nova original, nova paralela, retirada de veículo…) que ele escolhe em
 *    cada oferta. É por elas que um mesmo item pode receber mais de uma oferta; a marcação
 *    "exige foto" obriga a foto da peça (a retirada, decisão de Compras).
 *
 * A senha padrão do primeiro acesso saiu na at-17 (ADR 0015 do Portal): o link não pede senha.
 *
 * Estilo: nada de CSS próprio — card, hint, inputs e botões vêm de `adminSistema.css`, e a tabela é
 * a do padrão do sistema (`Tabela*`), importados pela página hospedeira.
 */

const CHAVE_CONDICOES = 'CONDICOES_PECA';
const MAX_CARACTERES = 80;

interface Condicao {
  nome: string;
  exigeFoto: boolean;
  ativa: boolean;
}

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'Condições da peça',
    conteudo: (
      <p>
        Em cada item, o fornecedor escolhe a condição da peça que está oferecendo. Ele pode mandar{' '}
        <strong>mais de uma oferta para o mesmo item</strong>, uma por condição (por exemplo, a
        original e a paralela), e o comparador mostra todas.
      </p>
    ),
  },
  {
    titulo: 'Exige foto',
    conteudo: (
      <p>
        Condição marcada com <strong>Exige foto</strong> só é aceita com ao menos uma foto da peça.
        É o caso da peça retirada de outro veículo: sem a foto, o comprador não tem como avaliar o
        estado dela.
      </p>
    ),
  },
  {
    titulo: 'Desativar e editar',
    conteudo: (
      <p>
        Condição desativada deixa de aparecer para o fornecedor, mas as propostas que já a usaram
        continuam mostrando o nome dela. Mudanças valem <strong>daqui em diante</strong>.
      </p>
    ),
  },
];

function lerCondicoes(bruto: string | undefined): Condicao[] {
  if (!bruto) return [];
  const valor = JSON.parse(bruto);
  if (!Array.isArray(valor)) return [];
  return valor.filter(
    (c: unknown): c is Condicao =>
      !!c && typeof c === 'object' && typeof (c as Condicao).nome === 'string',
  ).map((c: Condicao) => ({ nome: c.nome, exigeFoto: !!c.exigeFoto, ativa: c.ativa !== false }));
}

interface RespostaFornecedorSectionProps {
  /** Só ADMIN/TI gravam configuração no Portal (`PUT /settings/:key`) — ver FormasPagamentoSection. */
  podeEditar: boolean;
}

const RespostaFornecedorSection: React.FC<RespostaFornecedorSectionProps> = ({ podeEditar }) => {
  const [condicoes, setCondicoes] = useState<Condicao[]>([]);
  const [nova, setNova] = useState('');
  const [novaExigeFoto, setNovaExigeFoto] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [ajudaAberta, setAjudaAberta] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const settings = await fetchSettingsPortal();
      setCondicoes(lerCondicoes(settings[CHAVE_CONDICOES]));
      setErro(false);
    } catch (error) {
      // Lista vazia por erro seria lida como "não há condições" (regra da at-60).
      setCondicoes([]);
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar a configuração da resposta do fornecedor. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function salvarCondicoes(lista: Condicao[], mensagem: string) {
    setSalvando(true);
    try {
      await atualizarSettingPortal(CHAVE_CONDICOES, JSON.stringify(lista));
      setCondicoes(lista);
      notify.success(mensagem);
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível salvar as condições da peça.');
    } finally {
      setSalvando(false);
    }
  }

  function adicionar() {
    const limpa = nova.trim();
    if (!limpa) return;
    if (limpa.length > MAX_CARACTERES) {
      notify.warning(`Cada condição pode ter no máximo ${MAX_CARACTERES} caracteres.`);
      return;
    }
    if (condicoes.some((c) => c.nome.toLowerCase() === limpa.toLowerCase())) {
      notify.warning('Esta condição já está na lista.');
      return;
    }
    setNova('');
    setNovaExigeFoto(false);
    salvarCondicoes([...condicoes, { nome: limpa, exigeFoto: novaExigeFoto, ativa: true }], 'Condição adicionada.');
  }

  function alternar(nome: string, campo: 'exigeFoto' | 'ativa') {
    const lista = condicoes.map((c) => (c.nome === nome ? { ...c, [campo]: !c[campo] } : c));
    salvarCondicoes(lista, 'Condição atualizada.');
  }

  const colunas = '3fr 1fr 1fr';

  return (
    <>
      <div className="admin-card">
        <div className="admin-card-title">
          Condições da peça
          <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setAjudaAberta(true)}>
            ?
          </button>
        </div>
        <p className="admin-hint" style={{ marginTop: 0 }}>
          O fornecedor escolhe uma destas condições em cada oferta e pode mandar mais de uma oferta por item. Condição
          com <strong>Exige foto</strong> só é aceita com foto da peça.
        </p>

        {erro ? (
          <p className="erro-de-tela">
            Não foi possível carregar as condições da peça, então não dá para saber o que o fornecedor está vendo hoje.
            Recarregue a página antes de editar; se persistir, acione a TI.
          </p>
        ) : carregando ? (
          <p className="admin-hint">Carregando...</p>
        ) : (
          <>
            {condicoes.length === 0 && (
              <p className="admin-hint">
                Nenhuma condição cadastrada: o fornecedor manda uma oferta por item, sem escolher condição.
              </p>
            )}

            {condicoes.length > 0 && (
              <div className="TabelaScroll" style={{ width: '100%', boxSizing: 'border-box' }}>
                <div
                  className="TabelaContent"
                  style={{ ['--table-columns' as any]: colunas, width: '100%', minWidth: '480px' }}
                >
                  <div className="Tabela TabelaHeader">
                    <div className="Placa">
                      <div className="TTulo">
                        <div className="Opcoes">Condição</div>
                        <div className="Opcoes">Exige foto</div>
                        <div className="Opcoes">Situação</div>
                      </div>
                    </div>
                  </div>

                  {condicoes.map((c) => (
                    <div key={c.nome} className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                      <div className="Item">
                        <div className="Data" title={c.nome} style={{ fontWeight: 600 }}>
                          {c.nome}
                        </div>
                      </div>
                      <div className="Item">
                        {podeEditar ? (
                          <label className="Data">
                            <input
                              type="checkbox"
                              checked={c.exigeFoto}
                              disabled={salvando}
                              onChange={() => alternar(c.nome, 'exigeFoto')}
                              aria-label={`Exige foto: ${c.nome}`}
                            />{' '}
                            {c.exigeFoto ? 'Sim' : 'Não'}
                          </label>
                        ) : (
                          <div className="Data">{c.exigeFoto ? 'Sim' : 'Não'}</div>
                        )}
                      </div>
                      <div className="Item">
                        {podeEditar ? (
                          <button
                            type="button"
                            className="admin-btn admin-btn-secondary"
                            disabled={salvando}
                            onClick={() => alternar(c.nome, 'ativa')}
                          >
                            {c.ativa ? 'Desativar' : 'Ativar'}
                          </button>
                        ) : (
                          <div className="Data">{c.ativa ? 'Ativa' : 'Inativa'}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {podeEditar && (
              <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  className="admin-input"
                  placeholder="Ex.: Recondicionada"
                  maxLength={MAX_CARACTERES}
                  value={nova}
                  disabled={salvando}
                  onChange={(e) => setNova(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') adicionar();
                  }}
                />
                <label className="admin-label">
                  <input
                    type="checkbox"
                    checked={novaExigeFoto}
                    disabled={salvando}
                    onChange={(e) => setNovaExigeFoto(e.target.checked)}
                  />{' '}
                  Exige foto
                </label>
                <button
                  type="button"
                  className="admin-btn admin-btn-primary"
                  disabled={salvando || !nova.trim()}
                  onClick={adicionar}
                >
                  Adicionar
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <ComoFunciona
        isOpen={ajudaAberta}
        onClose={() => setAjudaAberta(false)}
        titulo="Como Funciona - Resposta do fornecedor"
        secoes={helpSections}
      />
    </>
  );
};

export default RespostaFornecedorSection;
